import { useEffect, useMemo, useRef, useState } from "react";
import type {
  AgentWorkbenchPatch,
  AgentWorkSurfaceManifest,
  ContentPiece,
} from "../../../../shared/types";
import { Badge } from "../ui/Badge";
import { Button } from "../ui/Button";
import { PLATFORM_LIMITS, platformLabels } from "./ContentCard";
import { cn } from "../../lib/utils";
import {
  useApplyContentPieceWorkbenchPatch,
  useContentPieceWorkbench,
  useProposeContentPieceWorkbenchPatch,
} from "../../hooks/useContent";
import { WorkbenchPatchPreview } from "../workbench/WorkbenchPatchPreview";

interface ContentEditWorkspaceProps {
  piece: ContentPiece;
  onSave: (id: string, content: string) => Promise<unknown> | void;
  onBackToReview: () => void;
  requestedInspectorTab?: {
    tab: "edit" | "voice" | "facts";
    nonce: number;
  } | null;
}

type GuidanceTone = "warning" | "info" | "success" | "error";

interface GuidanceItem {
  tone: GuidanceTone;
  title: string;
  count: number;
  action: string;
  body: string;
}

interface SupportState {
  tone: GuidanceTone;
  title: string;
  action: string;
  body: string;
}

function countWords(content: string): number {
  return content.trim().split(/\s+/).filter(Boolean).length;
}

function estimateVoiceFit(content: string): number {
  const vocabulary = [
    "handoff",
    "agent",
    "workflow",
    "structured data",
    "intervention",
    "browser automation",
    "trust",
  ];
  const lower = content.toLowerCase();
  const matches = vocabulary.filter((term) => lower.includes(term)).length;
  return Math.min(96, 62 + matches * 5);
}

function buildGuidance(content: string, overLimit: boolean): GuidanceItem[] {
  const sentences = content.split(/[.!?]+/).map((part) => part.trim()).filter(Boolean);
  const longSentenceCount = sentences.filter((sentence) => sentence.split(/\s+/).length > 28).length;
  const lower = content.toLowerCase();
  const hasHandoff = lower.includes("handoff") || lower.includes("intervention");

  return [
    {
      tone: longSentenceCount > 0 ? "warning" : "success",
      title: "Long sentence",
      count: longSentenceCount,
      action: longSentenceCount > 0 ? "Split" : "Clear",
      body:
        longSentenceCount > 0
          ? "One sentence is carrying several browser failure modes. Split it or make the list scannable."
          : "Sentence length is in a usable range for a technical LinkedIn post.",
    },
    {
      tone: hasHandoff ? "success" : "info",
      title: "Voice opportunity",
      count: hasHandoff ? 0 : 1,
      action: hasHandoff ? "Keep" : "Apply",
      body:
        "ThinkBrowse voice should use concrete terms like handoff, workflow, structured data, intervention, and trust.",
    },
    {
      tone: "info",
      title: "Strong line",
      // Informational guidance, not an actionable issue — excluded from the Issues tally.
      count: 0,
      action: "Keep",
      body: "The best line should state the operational point of view, not just describe agent automation.",
    },
    {
      tone: overLimit ? "error" : "warning",
      title: "Approval reset",
      // Only a real blocker when the draft is over the platform limit; otherwise informational.
      count: overLimit ? 1 : 0,
      action: "Required",
      body:
        overLimit
          ? "This draft is over the platform limit. Save is blocked until it is shortened."
          : "Saving edits returns this piece to Draft so the operator can explicitly approve the revised copy.",
    },
  ];
}

const issueStyles: Record<GuidanceTone, string> = {
  warning: "border-amber-200 bg-amber-50 text-amber-950",
  info: "border-brand-200 bg-brand-50 text-brand-950",
  success: "border-green-200 bg-green-50 text-green-950",
  error: "border-red-200 bg-red-50 text-red-950",
};

export function ContentEditWorkspace({
  piece,
  onSave,
  onBackToReview,
  requestedInspectorTab,
}: ContentEditWorkspaceProps) {
  const [content, setContent] = useState(piece.content);
  const [savedContent, setSavedContent] = useState(piece.content);
  const [proposal, setProposal] = useState<AgentWorkbenchPatch | null>(null);
  const [proposalError, setProposalError] = useState<string | null>(null);
  const [lastRequest, setLastRequest] = useState<{
    target_section_id?: string | null;
    quick_action_id?: string | null;
    instruction?: string | null;
  } | null>(null);
  const [showOriginal, setShowOriginal] = useState(false);
  const [inspectorTab, setInspectorTab] = useState<"edit" | "voice" | "facts">("edit");
  const [saving, setSaving] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const workbench = useContentPieceWorkbench(piece.id);
  const proposePatch = useProposeContentPieceWorkbenchPatch(piece.id);
  const applyPatch = useApplyContentPieceWorkbenchPatch(piece.id, piece.run_id);
  const limit = PLATFORM_LIMITS[piece.platform] ?? 5000;
  const characterCount = content.length;
  const isOverLimit = characterCount > limit;
  const dirty = content !== savedContent;
  const words = useMemo(() => countWords(content), [content]);
  const voiceFit = useMemo(() => estimateVoiceFit(content), [content]);
  const guidance = useMemo(
    () => buildGuidance(content, isOverLimit),
    [content, isOverLimit],
  );

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, [content]);

  useEffect(() => {
    // Only resync when a DIFFERENT piece is opened — keyed on piece.id, not piece.content,
    // so a background refetch (same piece) can't clobber the operator's unsaved edits.
    // handleSave/applyPatch already update savedContent locally on save.
    setContent(piece.content);
    setSavedContent(piece.content);
    setProposal(null);
    setProposalError(null);
    setLastRequest(null);
    setShowOriginal(false);
    setInspectorTab("edit");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [piece.id]);

  useEffect(() => {
    if (!requestedInspectorTab) return;
    setInspectorTab(requestedInspectorTab.tab);
  }, [requestedInspectorTab]);

  async function handleSave() {
    const trimmed = content.trim();
    if (!trimmed || isOverLimit || !dirty || saving) return;
    setProposal(null);
    setSaving(true);
    try {
      await onSave(piece.id, trimmed);
      // Mark saved only after the server write succeeds — a failed PUT keeps the
      // draft dirty and the editor open so the operator can retry (no false "saved").
      // Normalize content to the trimmed value too, or a trailing-whitespace save
      // leaves content !== savedContent and the draft reads as still-dirty.
      setContent(trimmed);
      setSavedContent(trimmed);
    } catch {
      // Stay dirty; the parent keeps the workspace open on error.
    } finally {
      setSaving(false);
    }
  }

  async function requestPatch(request: {
    target_section_id?: string | null;
    quick_action_id?: string | null;
    instruction?: string | null;
  }) {
    setProposalError(null);
    setLastRequest(request);
    try {
      const response = await proposePatch.mutateAsync(request);
      setProposal(response.data);
    } catch (error) {
      setProposal(null);
      setProposalError(error instanceof Error ? error.message : "Could not generate a workbench patch.");
    }
  }

  async function handleApplyProposal() {
    if (!proposal) return;
    try {
      const response = await applyPatch.mutateAsync(proposal);
      const nextContent = readManifestContent(response.data);
      if (nextContent) {
        setContent(nextContent);
        setSavedContent(nextContent);
      }
      setProposal(null);
      setProposalError(null);
    } catch (error) {
      setProposalError(error instanceof Error ? error.message : "Could not apply the workbench patch.");
    }
  }

  function handleAskAgain() {
    if (!lastRequest) return;
    void requestPatch(lastRequest);
  }

  const manifest = workbench.data?.data;
  const voiceContext = readMarkdownSection(manifest, "voice_context");
  const sourceContext = readSourceContext(manifest);
  const evidenceCount = manifest?.evidence_refs?.length ?? 0;
  const hasVoiceGuidance = hasMeaningfulVoiceGuidance(voiceContext);
  const sourceSupportState = buildSourceSupportState(sourceContext, evidenceCount);

  return (
    <div className="grid gap-6 p-4 lg:grid-cols-[minmax(0,1fr)_340px] lg:p-6">
      <section className="min-w-0 rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-4">
          {/* FR-10: no dead formatting controls. Platform is shown once, in the meta-bar below. */}
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                void requestPatch({
                  target_section_id: "content",
                  quick_action_id: "tighten-copy",
                })}
              disabled={proposePatch.isPending || workbench.isLoading}
              loading={proposePatch.isPending}
            >
              Ask AI to tighten
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowOriginal((current) => !current)}
            >
              {showOriginal ? "Hide original" : "Compare original"}
            </Button>
          </div>
        </div>

        <div className="mx-auto max-w-4xl px-5 py-8 md:px-10 md:py-12">
          <div className="mb-5 flex flex-wrap gap-2">
            <Badge variant="info">{platformLabels[piece.platform] ?? piece.platform}</Badge>
            <Badge variant={isOverLimit ? "error" : "default"}>
              {characterCount}/{limit} characters
            </Badge>
            <Badge variant={dirty ? "warning" : "default"}>
              {dirty ? "Needs re-approval" : "Unchanged"}
            </Badge>
          </div>

          <label htmlFor={`content-edit-${piece.id}`} className="sr-only">
            Edit {platformLabels[piece.platform] ?? piece.platform} content
          </label>
          <textarea
            ref={textareaRef}
            id={`content-edit-${piece.id}`}
            value={content}
            onChange={(event) => setContent(event.target.value)}
            className="min-h-[420px] w-full resize-none border-0 bg-transparent text-[19px] leading-8 text-slate-700 outline-none placeholder:text-slate-400 md:text-[22px] md:leading-9"
            placeholder="Write the revised post..."
          />
          {showOriginal
            ? (
              <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                  Original AI Draft
                </p>
                <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-700">
                  {piece.ai_draft || piece.content}
                </p>
              </div>
            )
            : null}
          {proposal && manifest
            ? (
              <div className="mt-6">
                <WorkbenchPatchPreview
                  manifest={manifest}
                  patch={proposal}
                  onApply={handleApplyProposal}
                  onAskAgain={handleAskAgain}
                  onReject={() => setProposal(null)}
                  onCancel={() => setProposal(null)}
                  applying={applyPatch.isPending}
                  retrying={proposePatch.isPending}
                />
              </div>
            )
            : null}
          {proposalError
            ? (
              <div className="mt-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                {proposalError}
              </div>
            )
            : null}
        </div>
      </section>

      <aside className="rounded-xl border border-slate-200 bg-white shadow-sm lg:sticky lg:top-6 lg:self-start">
        <div className="grid grid-cols-3 gap-1 border-b border-slate-200 bg-slate-50 p-2">
          <button
            type="button"
            onClick={() => setInspectorTab("edit")}
            className={cn(
              "rounded-lg px-3 py-2 text-sm font-semibold",
              inspectorTab === "edit"
                ? "bg-white text-slate-950 shadow-sm"
                : "text-slate-600",
            )}
          >
            Edit
          </button>
          <button
            type="button"
            onClick={() => setInspectorTab("voice")}
            className={cn(
              "rounded-lg px-3 py-2 text-sm font-semibold",
              inspectorTab === "voice"
                ? "bg-white text-slate-950 shadow-sm"
                : "text-slate-600",
            )}
          >
            Voice
          </button>
          <button
            type="button"
            onClick={() => setInspectorTab("facts")}
            className={cn(
              "rounded-lg px-3 py-2 text-sm font-semibold",
              inspectorTab === "facts"
                ? "bg-white text-slate-950 shadow-sm"
                : "text-slate-600",
            )}
          >
            Facts
          </button>
        </div>

        <div className="space-y-4 p-4">
          {inspectorTab === "edit" ? (
            <>
              <section>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-950">Readability</h2>
                    <p className="mt-1 text-sm leading-6 text-slate-600">
                      Placeholder grade — real readability analysis lands in a follow-up. Use the issues below as edit prompts.
                    </p>
                  </div>
                  <span className="text-right text-3xl font-bold text-success">
                    B+
                    <span className="block text-[10px] font-medium uppercase tracking-wide text-slate-400">
                      preview
                    </span>
                  </span>
                </div>
              </section>

              <section className="grid grid-cols-2 gap-2">
                <Stat label="Words" value={String(words)} />
                <Stat label="Voice fit (preview)" value={`${voiceFit}%`} />
                <Stat label="Issues" value={String(guidance.filter((item) => item.count > 0).length)} />
                <Stat label="Evidence" value={String(evidenceCount)} />
              </section>

              <section className="space-y-2">
                {guidance.map((item) => (
                  <div
                    key={item.title}
                    className={cn("rounded-lg border p-3", issueStyles[item.tone])}
                  >
                    <div className="flex items-center justify-between gap-3 text-sm font-semibold">
                      <span>
                        <span className="mr-2 inline-flex h-6 min-w-6 items-center justify-center rounded-md bg-white/70 font-data text-[11px]">
                          {item.count}
                        </span>
                        {item.title}
                      </span>
                      <span className="text-xs">{item.action}</span>
                    </div>
                    <p className="mt-2 text-sm leading-5 text-slate-700">{item.body}</p>
                  </div>
                ))}
              </section>
            </>
          ) : null}

          {inspectorTab === "voice" ? (
            <section className="space-y-3">
              <div>
                <h2 className="text-lg font-semibold text-slate-950">Brand voice context</h2>
                <p className="mt-1 text-sm leading-6 text-slate-600">
                  This is the stored guidance the Workbench can use when rewriting the draft.
                </p>
              </div>
              <div className={cn("rounded-lg border p-3", issueStyles[hasVoiceGuidance ? "success" : "warning"])}>
                <div className="flex items-center justify-between gap-3 text-sm font-semibold">
                  <span>{hasVoiceGuidance ? "Voice guidance loaded" : "Voice guidance missing"}</span>
                  <span className="text-xs">{hasVoiceGuidance ? "Use it" : "Add it first"}</span>
                </div>
                <p className="mt-2 text-sm leading-5 text-slate-700">
                  {hasVoiceGuidance
                    ? "Use this guidance when tightening or approving. If the copy drifts from it, edit the draft or ask for another patch."
                    : "Do not treat AI rewrite output as on-voice yet. Add a brand voice or manually edit the copy before approval."}
                </p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <p className="whitespace-pre-wrap text-sm leading-6 text-slate-700">
                  {voiceContext || "No brand voice is available for this brand yet."}
                </p>
              </div>
            </section>
          ) : null}

          {inspectorTab === "facts" ? (
            <section className="space-y-3">
              <div>
                <h2 className="text-lg font-semibold text-slate-950">Source materials</h2>
                <p className="mt-1 text-sm leading-6 text-slate-600">
                  Review the selected idea and run context before approving or tightening claims.
                </p>
              </div>
              <div className={cn("rounded-lg border p-3", issueStyles[sourceSupportState.tone])}>
                <div className="flex items-center justify-between gap-3 text-sm font-semibold">
                  <span>{sourceSupportState.title}</span>
                  <span className="text-xs">{sourceSupportState.action}</span>
                </div>
                <p className="mt-2 text-sm leading-5 text-slate-700">{sourceSupportState.body}</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Stat label="Run" value={sourceContext.runId || "Unknown"} />
                <Stat label="Evidence" value={String(evidenceCount)} />
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                  Selected idea
                </p>
                <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-700">
                  {sourceContext.selectedIdea
                    ? JSON.stringify(sourceContext.selectedIdea, null, 2)
                    : "No selected idea is available on this run yet."}
                </p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                  Run checkpoint
                </p>
                <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-700">
                  {sourceContext.checkpoint
                    ? JSON.stringify(sourceContext.checkpoint, null, 2)
                    : "No checkpoint context is available for this draft."}
                </p>
              </div>
            </section>
          ) : null}
        </div>
      </aside>

      <div className="lg:col-span-2">
        <div className="sticky bottom-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white/95 p-4 shadow-lg backdrop-blur">
          <div>
            <p className="text-sm font-semibold text-slate-950">Editing {platformLabels[piece.platform] ?? piece.platform} draft</p>
            <p className="text-sm text-slate-600">
              Save changes, then approve this draft from the review tab before publishing.
              {workbench.isError ? " Workbench context could not be loaded, so AI patching is unavailable." : ""}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="ghost" size="sm" onClick={onBackToReview}>
              Back to review
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSave}
              disabled={!dirty || !content.trim() || isOverLimit || saving}
            >
              {saving ? "Saving…" : "Save and return to review"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
      <span className="font-data text-[10px] font-medium uppercase tracking-[0.08em] text-slate-500">
        {label}
      </span>
      <strong className="mt-1 block text-lg font-semibold text-slate-950">
        {value}
      </strong>
    </div>
  );
}

function readManifestContent(manifest: AgentWorkSurfaceManifest): string {
  const section = manifest.sections.find((candidate) => candidate.id === "content");
  if (section?.content.kind !== "text") {
    // Surface a schema mismatch rather than silently rendering an empty editor.
    console.warn("[ContentEditWorkspace] manifest 'content' section missing or not text-kind");
    return "";
  }
  return section.content.text;
}

function readMarkdownSection(
  manifest: AgentWorkSurfaceManifest | undefined,
  sectionId: string,
): string {
  const section = manifest?.sections.find((candidate) => candidate.id === sectionId);
  return section?.content.kind === "markdown" ? section.content.markdown : "";
}

function readSourceContext(manifest: AgentWorkSurfaceManifest | undefined): {
  runId: string;
  selectedIdea: unknown;
  checkpoint: unknown;
} {
  const section = manifest?.sections.find((candidate) => candidate.id === "source_context");
  if (section?.content.kind !== "json") {
    return { runId: "", selectedIdea: null, checkpoint: null };
  }

  const value = section.content.value;
  return {
    runId: typeof value.run_id === "string" ? value.run_id : "",
    selectedIdea: value.selected_idea ?? null,
    checkpoint: value.checkpoint ?? null,
  };
}

function hasMeaningfulVoiceGuidance(voiceContext: string): boolean {
  const normalized = voiceContext.trim().toLowerCase();
  return normalized.length > 0 && normalized !== "no brand voice is available for this brand yet.";
}

function buildSourceSupportState(
  sourceContext: { runId: string; selectedIdea: unknown; checkpoint: unknown },
  evidenceCount: number,
): SupportState {
  const hasSelectedIdea = sourceContext.selectedIdea != null;
  const hasCheckpoint = sourceContext.checkpoint != null;
  const hasEvidence = evidenceCount > 0;

  if (hasSelectedIdea && hasCheckpoint && hasEvidence) {
    return {
      tone: "success",
      title: "Support context loaded",
      action: "Verify claim",
      body: "You have idea, checkpoint, and run evidence context. Verify the strongest claims against this material, then approve or revise.",
    };
  }

  if (hasCheckpoint || hasEvidence || hasSelectedIdea) {
    return {
      tone: "warning",
      title: "Support context is partial",
      action: "Edit or regenerate",
      body: "Some supporting data is present, but the draft is not fully grounded. Remove unsupported claims, inspect the available context, or regenerate after better inputs exist.",
    };
  }

  // FR-9: missing provenance does NOT block approval — the Review-tab gate is authoritative.
  // Surface it as informational lineage, not a hard "do not approve".
  return {
    tone: "info",
    title: "Source context unavailable",
    action: "Review manually",
    body: "No selected idea, checkpoint, or supporting evidence is loaded for this draft. Approval isn't blocked — use your judgment and verify any specific claims before publishing.",
  };
}
